/**
 * Billing Provider Discovery Hook
 *
 * Discovers whether billing is available for the current workspace
 * and which capabilities (checkout, portal) are enabled.
 *
 * Used by CreditsTab to conditionally show upgrade/manage CTAs.
 */

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { BillingProvider } from '@levelup/central-billing';
import type { RootState } from '../store';
import { fetchBillingProvider } from '../lib/centralBilling';
import { usePermissions } from './usePermissions';

export interface UseBillingProviderResult {
  provider: BillingProvider | null;
  isStripe: boolean;
  checkoutEnabled: boolean;
  portalEnabled: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useBillingProvider(): UseBillingProviderResult {
  const workspaceId = useSelector(
    (state: RootState) => state.boot.context?.currentWorkspace?.id ?? null,
  );
  const { hasWorkspacePermission } = usePermissions();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['billingProvider', workspaceId],
    queryFn: () => fetchBillingProvider(workspaceId!),
    enabled: !!workspaceId && hasWorkspacePermission('manageBilling'),
    staleTime: 300000,
    retry: 1,
  });

  return {
    provider: data?.provider ?? null,
    isStripe: data?.provider === 'stripe',
    checkoutEnabled: data?.checkoutEnabled ?? false,
    portalEnabled: data?.portalEnabled ?? false,
    isLoading,
    isError,
  };
}
